import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import type { Device } from '../../lib/types';
import {
  rescueTextStalledAdoptersMutation,
  type RescueTextStalledAdoptersData,
  type StalledReminderVariables,
} from '../../lib/rescueQueries';
import { newReminderRequestId } from '../../lib/stalledReminders';
import { withoutSmsOptOutLine } from '../../lib/smsText';
import { formatPhoneNumber } from '../../lib/utils';
import useStalledAdopters from '../../hooks/useStalledAdopters';
import { trackDeviceSmsSent } from '../../analytics';
import { useToast } from '../../components/Toast/ToastContext';
import Modal, { ModalTitle, ModalSubtitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import FiNumberNotice from './FiNumberNotice';
import SmsComposer from './SmsComposer';
import r from './StalledReminders.module.scss';

interface StalledSmsModalProps {
  /** The Stalled row the rescue picked. The modal mounts per row, so its state starts fresh each time. */
  device: Device;
  onClose: () => void;
}

/**
 * Send SMS from a Stalled row: the reminder Text all sends, as a body the rescue can rewrite, to one
 * adopter. An adopter with several stalled pets gets the one text that covers them all, recorded on each
 * of their transfers, so the row reads "Today" afterwards.
 */
export default function StalledSmsModal({ device, onClose }: StalledSmsModalProps) {
  const { showToast } = useToast();
  const { adopters, smsOptOutLine, loading, error } = useStalledAdopters();
  const [send, { loading: sending }] = useMutation<RescueTextStalledAdoptersData, StalledReminderVariables>(
    rescueTextStalledAdoptersMutation,
    { refetchQueries: 'active' },
  );
  const [requestId] = useState(newReminderRequestId);
  // Null until the rescue edits it, so the field starts from the reminder as soon as it loads.
  const [edited, setEdited] = useState<string | null>(null);

  const transferId = device._transferGraphqlId;
  const adopter = transferId ? adopters.find((candidate) => candidate.transferIds.includes(transferId)) : undefined;
  const body =
    edited ?? (adopter?.smsPreview && smsOptOutLine ? withoutSmsOptOutLine(adopter.smsPreview, smsOptOutLine) : '');

  async function handleSend() {
    if (!adopter) {
      return;
    }
    try {
      const { data } = await send({
        variables: { input: { transferIds: adopter.transferIds, requestId, message: body } },
      });
      const result = data?.rescueTextStalledAdopters;
      if (result?.sentCount) {
        trackDeviceSmsSent();
        showToast('SMS sent successfully');
      } else if (result?.optedOutCount) {
        showToast(`${adopter.displayName} has opted out of texts from Fi`);
      } else if (result?.failedCount) {
        showToast("Couldn't send the text. Try again in a few minutes.");
      } else {
        showToast('Nothing sent: this adoption is no longer stalled');
      }
      onClose();
    } catch (err) {
      showToast(`Failed to send SMS: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  const canSend = !!adopter?.smsPreview && !!smsOptOutLine && !adopter.smsOptedOut;

  function content() {
    if (loading) {
      return <p className={r.statusMessage}>Loading the adopter's details…</p>;
    }
    if (error) {
      return <p className={r.statusMessage}>Couldn't load the adopter's details. Close this and try again.</p>;
    }
    if (!adopter) {
      return <p className={r.statusMessage}>This adoption is no longer stalled, so there's no reminder to send.</p>;
    }
    if (!adopter.phoneNumber || !adopter.smsPreview) {
      return <ModalSubtitle>No phone number on file for {adopter.displayName}.</ModalSubtitle>;
    }
    if (adopter.smsOptedOut || !smsOptOutLine) {
      return <ModalSubtitle>{adopter.displayName} replied STOP and has opted out of texts from Fi.</ModalSubtitle>;
    }
    return (
      <>
        <ModalSubtitle>
          To: {adopter.displayName} · {formatPhoneNumber(adopter.phoneNumber)}
        </ModalSubtitle>
        <SmsComposer body={body} onChange={setEdited} optOutLine={smsOptOutLine} />
        <FiNumberNotice />
      </>
    );
  }

  return (
    <Modal open onClose={sending ? () => {} : onClose}>
      <ModalTitle>Send SMS</ModalTitle>
      {content()}
      <ModalActions>
        <Button variant="ghost" onClick={onClose} disabled={sending}>
          {canSend ? 'Cancel' : 'Close'}
        </Button>
        {canSend && (
          <Button variant="green" onClick={handleSend} disabled={sending || !body.trim()}>
            {sending ? 'Sending…' : 'Send SMS'}
          </Button>
        )}
      </ModalActions>
    </Modal>
  );
}
